/**
 * fareLadderView — pure view helpers for "every price a traveller can pay".
 *
 * No React in here. The components (FareLadder, FlyDaysStrip, LegOffers,
 * YourPriceControl, WatchingLine) only lay out what these functions decide, so
 * the wording and the rules are unit-tested in one place.
 *
 * House rules, enforced here rather than remembered at each call site:
 *  - A null in the contract means "we did not see it". It is never rendered as
 *    a cross, a zero, or a guess. Lines for null items are simply not produced.
 *  - A null extra price reads "price shown at booking", never $0.
 *  - Plain English, sentence case, no emoji.
 */
import type {
  ExtraKind,
  FareLevelChoice,
  PlanAward,
  PlanExtra,
  PlanFare,
  PlanFlyDays,
  PlanMemberPrice,
  PlanOffer,
  PlanPriceChoice,
  PlanYourPrice,
} from "@/lib/fareOptionsTypes";
import type { PlanFlight, PlanLeg } from "@/lib/types";

/* ── money and dates ────────────────────────────────────────────────────── */

/** "$312" for whole dollars, "$42.75" when there are cents. Never "$312.00". */
export function money(n: number): string {
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  return (
    "$" +
    (whole
      ? Math.round(n).toLocaleString("en-US")
      : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseDay(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** "Nov 28" from an ISO date or timestamp; read as a calendar date, never shifted by time zone. */
export function shortDate(iso: string | null | undefined): string | null {
  const d = parseDay(iso);
  return d ? MONTHS[d.getUTCMonth()] + " " + d.getUTCDate() : null;
}

/** "Wed, Nov 5". */
export function weekdayDate(iso: string | null | undefined): string | null {
  const d = parseDay(iso);
  return d ? WEEKDAYS[d.getUTCDay()] + ", " + MONTHS[d.getUTCMonth()] + " " + d.getUTCDate() : null;
}

/** 0 = Sunday … 6 = Saturday, or null when the date is not a date. */
export function weekdayIndex(iso: string | null | undefined): number | null {
  const d = parseDay(iso);
  return d ? d.getUTCDay() : null;
}

/* ── the fare ladder ────────────────────────────────────────────────────── */

/** Most fare levels shown by name before the rest fold into one "+N" chip. */
export const MAX_NAMED_LEVELS = 4;

/** Cheapest first; ties keep the airline's own order. Never mutates its input. */
export function sortFares(fares: readonly PlanFare[]): PlanFare[] {
  return fares
    .map((f, i) => ({ f, i }))
    .sort((a, b) => a.f.price - b.f.price || a.i - b.i)
    .map((x) => x.f);
}

/**
 * The airline's level name, shortened to fit a chip: "Basic Economy" -> "Basic",
 * "Main Cabin Extra" -> "Main Extra". Falls back to the full name rather than
 * ever producing an empty chip.
 */
export function shortLevel(level: string): string {
  const stripped = level
    .replace(/\b(economy|cabin|class|fare)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped || level.trim();
}

export type LadderChip =
  | { kind: "fare"; key: string; label: string; price: number; fare: PlanFare; yours: boolean }
  | { kind: "miles"; key: "miles"; label: "Miles"; miles: number; useMiles: boolean; award: PlanAward }
  | { kind: "more"; key: "more"; label: string; hidden: PlanFare[] };

/** "62,500 miles", abbreviated for a chip: "62.5k". */
export function milesShort(miles: number): string {
  if (miles >= 10000) return (Math.round(miles / 100) / 10).toString().replace(/\.0$/, "") + "k";
  return miles.toLocaleString("en-US");
}

/**
 * The chips under a flight row. Empty when there is nothing beyond the one
 * headline price to show (no fares, or a single fare), so the row looks
 * exactly as it did before. The Miles chip rides along only with a real ladder.
 */
export function ladderChips(
  flight: Pick<PlanFlight, "fares" | "award" | "yourPrice">,
): LadderChip[] {
  const fares = flight.fares ?? [];
  if (fares.length < 2) return [];
  const sorted = sortFares(fares);
  const named = sorted.length > MAX_NAMED_LEVELS ? sorted.slice(0, MAX_NAMED_LEVELS) : sorted;
  const hidden = sorted.slice(named.length);
  const yoursLevel = flight.yourPrice?.fareLevel ?? null;
  const chips: LadderChip[] = named.map((fare, i) => ({
    kind: "fare",
    key: "fare-" + i,
    label: shortLevel(fare.level),
    price: fare.price,
    fare,
    yours: yoursLevel != null && fare.level === yoursLevel,
  }));
  if (hidden.length > 0) {
    chips.push({ kind: "more", key: "more", label: "+" + hidden.length, hidden });
  }
  if (flight.award) {
    chips.push({
      kind: "miles",
      key: "miles",
      label: "Miles",
      miles: flight.award.miles,
      useMiles: flight.award.verdict === "use_miles",
      award: flight.award,
    });
  }
  return chips;
}

/** The fare the headline price is (the cheapest we scraped), else the cheapest. */
export function headlineFare(fares: readonly PlanFare[]): PlanFare | null {
  if (fares.length === 0) return null;
  return fares.find((f) => f.isHeadline) ?? sortFares(fares)[0];
}

/* ── what a fare level includes ─────────────────────────────────────────── */

export type IncludeState = "yes" | "no" | "info";
export interface IncludeLine {
  key: string;
  state: IncludeState;
  text: string;
}

/**
 * One tick/cross line per included item we actually saw. A null is skipped, so
 * a level we know little about shows little, and never implies a "no".
 */
export function includeLines(fare: Pick<PlanFare, "includes">): IncludeLine[] {
  const inc = fare.includes;
  const out: IncludeLine[] = [];
  if (inc.seatChoice === "free") out.push({ key: "seat", state: "yes", text: "Seat choice included" });
  else if (inc.seatChoice === "fee") out.push({ key: "seat", state: "no", text: "Seat choice costs extra" });
  else if (inc.seatChoice === "none") out.push({ key: "seat", state: "no", text: "No seat choice" });

  if (inc.changeable === true) out.push({ key: "change", state: "yes", text: "Changes allowed" });
  else if (inc.changeable === false) out.push({ key: "change", state: "no", text: "No changes" });

  if (inc.refundable === "cash") out.push({ key: "refund", state: "yes", text: "Refund to your card" });
  else if (inc.refundable === "credit") out.push({ key: "refund", state: "yes", text: "Refund as airline credit" });
  else if (inc.refundable === "none") out.push({ key: "refund", state: "no", text: "Not refundable" });

  if (inc.carryOn === true) out.push({ key: "carry", state: "yes", text: "Carry-on included" });
  else if (inc.carryOn === false) out.push({ key: "carry", state: "no", text: "No carry-on" });

  if (inc.checkedBags != null) {
    out.push(
      inc.checkedBags > 0
        ? { key: "bags", state: "yes", text: inc.checkedBags + (inc.checkedBags === 1 ? " checked bag included" : " checked bags included") }
        : { key: "bags", state: "no", text: "No checked bags" },
    );
  }
  if (inc.boarding && inc.boarding.trim()) {
    out.push({ key: "boarding", state: "info", text: "Boarding: " + inc.boarding.trim() });
  }
  (inc.notes ?? []).forEach((n, i) => {
    if (n && n.trim()) out.push({ key: "note-" + i, state: "info", text: n.trim() });
  });
  return out;
}

/**
 * One line comparing a level with the headline fare, naming only what the extra
 * money buys: "$35 more than Basic: changeable, so we can watch for drops."
 */
export function compareToHeadline(fare: PlanFare, headline: PlanFare | null): string {
  if (!headline || fare === headline) return "This is the lowest fare we found.";
  const diff = Math.round(fare.price - headline.price);
  const base = shortLevel(headline.level);
  if (diff === 0) return "Same price as " + base + ".";
  if (diff < 0) return money(-diff) + " less than " + base + ".";
  const a = fare.includes;
  const b = headline.includes;
  const gains: string[] = [];
  if ((a.refundable === "cash" || a.refundable === "credit") && b.refundable !== "cash" && b.refundable !== "credit") {
    gains.push("refundable");
  }
  if (a.seatChoice === "free" && b.seatChoice !== "free") gains.push("seat choice included");
  if ((a.checkedBags ?? 0) > (b.checkedBags ?? 0)) gains.push("bags included");
  if (a.carryOn === true && b.carryOn !== true) gains.push("carry-on included");
  const head = money(diff) + " more than " + base;
  // Changeable leads: it is what lets us watch for drops after booking.
  if (a.changeable === true && b.changeable !== true) {
    const also = gains.length > 0 ? "; also " + gains.join(", ") : "";
    return head + ": changeable, so we can watch for drops" + also + ".";
  }
  return gains.length > 0 ? head + ": " + gains.join(", ") + "." : head + ".";
}

/* ── miles ──────────────────────────────────────────────────────────────── */

export interface AwardView {
  price: string; // "62,500 miles + $5.60"
  value: string; // "1.9 cents per mile, against a typical 1.4"
  verdict: string;
}

function cents(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

export function awardView(award: PlanAward): AwardView {
  return {
    price: award.miles.toLocaleString("en-US") + " miles + " + money(award.cashCopay),
    value: cents(award.centsPerMile) + " cents per mile, against a typical " + cents(award.typicalCentsPerMile),
    verdict:
      award.verdict === "use_miles"
        ? "Worth using miles: you get more than they are usually worth."
        : "Better to pay cash: your miles are worth less here than usual.",
  };
}

/* ── extras ─────────────────────────────────────────────────────────────── */

const UNIT_WORDS: Record<PlanExtra["unit"], string> = {
  per_traveler_per_direction: "per traveller each way",
  per_traveler: "per traveller",
  per_booking: "per booking",
  per_year: "per year",
};

export function unitWords(unit: PlanExtra["unit"]): string {
  return UNIT_WORDS[unit];
}

/**
 * "$42.75 per traveller each way", "$30 to $45 per traveller each way", or
 * "price shown at booking" when we never captured one. Never "$0".
 */
export function extraPriceText(extra: Pick<PlanExtra, "price" | "priceMax" | "unit">): string {
  if (extra.price == null) return "price shown at booking";
  const range =
    extra.priceMax != null && extra.priceMax > extra.price
      ? money(extra.price) + " to " + money(extra.priceMax)
      : money(extra.price);
  return range + " " + unitWords(extra.unit);
}

export function sourceText(date: string | null | undefined): string {
  const d = shortDate(date);
  return d ? "Source, " + d : "Source";
}

/** The kinds the traveller can switch on in "Your price". Bags have their own stepper. */
export const CHOOSABLE_EXTRA_KINDS: readonly ExtraKind[] = [
  "seat",
  "priority",
  "disruption_cover",
  "travel_insurance",
];

const EXTRA_SHORT: Partial<Record<ExtraKind, string>> = {
  seat: "seat",
  priority: "priority boarding",
  disruption_cover: "disruption cover",
  travel_insurance: "travel insurance",
  bag_checked: "checked bag",
  bag_carry_on: "carry-on",
  change: "changes",
};

export function extraShortName(kind: ExtraKind, fallback?: string): string {
  return EXTRA_SHORT[kind] ?? (fallback ? fallback.toLowerCase() : "extra");
}

/**
 * The extras a traveller can toggle for a leg: the focus flight's first, then any
 * other flight's, one per kind. Bags, change fees and bundles are not toggles.
 */
export function choosableExtras(leg: Pick<PlanLeg, "focus" | "matrix">): PlanExtra[] {
  const flights: PlanFlight[] = [];
  if (leg.focus) flights.push(leg.focus);
  for (const g of leg.matrix ?? []) for (const f of g.flights ?? []) flights.push(f);
  const seen = new Map<ExtraKind, PlanExtra>();
  for (const f of flights) {
    for (const e of f.extras ?? []) {
      if (CHOOSABLE_EXTRA_KINDS.includes(e.kind) && !seen.has(e.kind)) seen.set(e.kind, e);
    }
  }
  return CHOOSABLE_EXTRA_KINDS.flatMap((k) => (seen.has(k) ? [seen.get(k) as PlanExtra] : []));
}

/* ── member price ───────────────────────────────────────────────────────── */

export interface MemberLine {
  text: string;
  tone: "good" | "quiet";
}

/**
 * One quiet line under the ladder, only when it helps: joining pays for itself
 * on this trip (green), or the traveller already holds the membership (just
 * their price). A "you are $21 behind" line is noise, so it is never shown.
 */
export function memberLine(mp: PlanMemberPrice): MemberLine | null {
  if (mp.alreadyMember) return { text: "Your " + mp.program + " price " + money(mp.price) + ".", tone: "quiet" };
  if (!mp.worthIt) return null;
  const net = Math.round(mp.netSavingThisTrip);
  const lead = mp.program + " price " + money(mp.price) + ". Membership " + money(mp.membershipCost) + ", so ";
  return { text: lead + (net > 0 ? "you are " + money(net) + " ahead on this trip." : "it pays for itself on this trip."), tone: "good" };
}

/* ── your price ─────────────────────────────────────────────────────────── */

/** The price a row shows: the traveller's own, else the headline fare. */
export function rowPrice(flight: Pick<PlanFlight, "price" | "yourPrice">): number | null {
  return flight.yourPrice ? flight.yourPrice.total : flight.price;
}

/**
 * The quiet label beside the one price, saying what it includes: "Main · 1 bag".
 * Level and bags only.
 * Null when the flight has no "your price" (the row then looks as before).
 */
export function yourPriceLabel(
  flight: Pick<PlanFlight, "yourPrice">,
  choice?: Pick<PlanPriceChoice, "checkedBags"> | null,
): string | null {
  const yp = flight.yourPrice;
  if (!yp) return null;
  const parts = [shortLevel(yp.fareLevel)];
  const bags = choice?.checkedBags ?? 0;
  if (bags > 0) parts.push(bags + (bags === 1 ? " bag" : " bags"));
  // Extras (priced or not) live in the detail panel, so this label never truncates mid-word.
  return parts.join(" · ");
}

export interface BreakdownLine {
  key: string;
  label: string;
  /** Right-hand text: an amount, or "priced at booking". */
  amount: string;
}

export interface YourPriceBreakdown {
  lines: BreakdownLine[];
  total: string;
  promo: string | null;
}

/** The lines behind the one number: fare, each chosen extra, any promo, total. */
export function yourPriceBreakdown(yp: PlanYourPrice, fareAmount: number | null): YourPriceBreakdown {
  const lines: BreakdownLine[] = [];
  const extrasTotal = yp.extrasIncluded.reduce((n, e) => n + (e.price ?? 0), 0);
  const promoSaving = yp.promo ? yp.promo.saving : 0;
  // The fare line is derived (total minus the pieces) when the caller has no
  // exact fare for the level, so the lines always add up to the total shown.
  const fare = fareAmount ?? yp.total - extrasTotal + promoSaving;
  lines.push({ key: "fare", label: shortLevel(yp.fareLevel) + " fare", amount: money(fare) });
  for (const e of yp.extrasIncluded) {
    lines.push({
      key: "extra-" + e.kind + "-" + e.name,
      label: e.name,
      amount: e.price == null ? "priced at booking" : money(e.price),
    });
  }
  for (const name of yp.unpricedExtras) {
    lines.push({ key: "unpriced-" + name, label: "+ " + name.toLowerCase(), amount: "priced at booking" });
  }
  return {
    lines,
    total: money(yp.total),
    promo: yp.promo ? "with code " + yp.promo.code + ", " + money(yp.promo.saving) + " less" : null,
  };
}

/* ── fly-days ───────────────────────────────────────────────────────────── */

export interface FlyDayCell {
  index: number;
  letter: string;
  name: string;
  flies: boolean;
  isLegDay: boolean;
}

const LETTERS = ["S", "M", "T", "W", "T", "F", "S"];
const LONG_WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export interface FlyDaysView {
  cells: FlyDayCell[];
  heading: string;
  notOnDate: boolean;
  nearest: { date: string; label: string; price: string | null }[];
  summary: string; // for assistive tech
}

export function flyDaysView(fd: PlanFlyDays, legDate: string | null | undefined): FlyDaysView {
  const legDay = weekdayIndex(legDate);
  const cells = LETTERS.map((letter, index) => ({
    index,
    letter,
    name: LONG_WEEKDAYS[index],
    flies: fd.weekdays.includes(index),
    isLegDay: legDay === index,
  }));
  const flying = cells.filter((c) => c.flies).map((c) => WEEKDAYS[c.index]);
  const notOnDate = fd.flysOnLegDate === false;
  const nearest = (fd.nearest ?? []).flatMap((n) => {
    const label = weekdayDate(n.date);
    return label ? [{ date: n.date, label, price: n.price != null ? money(n.price) : null }] : [];
  });
  return {
    cells,
    heading: fd.carrierName + " flies this route on",
    notOnDate,
    nearest,
    summary:
      fd.carrierName + " flies on " + (flying.length ? flying.join(", ") : "no days we know of") +
      (notOnDate ? ". It does not fly your date." : "."),
  };
}

/* ── offers ─────────────────────────────────────────────────────────────── */

export interface OfferView {
  id: string;
  title: string;
  whyYou: string;
  bookBy: string | null; // "Book by Nov 28"
  code: string | null;
  sourceLabel: string;
  sourceUrl: string;
}

export function offerView(o: PlanOffer): OfferView {
  const d = shortDate(o.bookBy);
  const code = o.code && o.code.trim() ? o.code.trim() : null;
  return {
    id: o.offerId,
    title: o.title,
    whyYou: o.whyYou,
    bookBy: d ? "Book by " + d : null,
    code,
    sourceLabel: sourceText(o.verifiedAt),
    sourceUrl: o.sourceUrl,
  };
}

/* ── "your price" choices ───────────────────────────────────────────────── */

export const DEFAULT_PRICE_CHOICE: PlanPriceChoice = { fareLevel: "cheapest", extras: [], checkedBags: 0 };
export const MAX_CHECKED_BAGS = 2;

export const FARE_CHOICES: { value: FareLevelChoice; label: string; meaning: string }[] = [
  { value: "cheapest", label: "Cheapest", meaning: "The lowest fare, with the airline's basic rules." },
  { value: "changeable", label: "Can change it", meaning: "Costs a little more, and plans can move." },
  { value: "extra_legroom", label: "Extra legroom", meaning: "More room to sit, on the same flight." },
  { value: "premium", label: "Premium", meaning: "The top cabin we can price." },
];

export function stepBags(current: number, delta: 1 | -1): number {
  return Math.min(MAX_CHECKED_BAGS, Math.max(0, Math.round(current) + delta));
}

export function bagsText(n: number): string {
  return n === 0 ? "No bags" : n === 1 ? "1 bag" : n + " bags";
}

export function toggleExtra(extras: readonly ExtraKind[], kind: ExtraKind): ExtraKind[] {
  return extras.includes(kind) ? extras.filter((k) => k !== kind) : [...extras, kind];
}

export function sameChoice(a: PlanPriceChoice, b: PlanPriceChoice): boolean {
  if (a.fareLevel !== b.fareLevel || a.checkedBags !== b.checkedBags) return false;
  if (a.extras.length !== b.extras.length) return false;
  const set = new Set(a.extras);
  return b.extras.every((k) => set.has(k));
}

/** Escape closes the panel; nothing else is intercepted (radios keep their arrow keys). */
export function panelKeyAction(key: string): "close" | null {
  return key === "Escape" ? "close" : null;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Save the choice, then refresh the plan so every flight re-prices. The caller
 * passes the two effects in, which keeps this testable without a browser.
 */
export async function submitPriceChoice(
  save: (choice: PlanPriceChoice) => Promise<unknown>,
  refresh: () => Promise<unknown> | unknown,
  choice: PlanPriceChoice,
): Promise<"saved" | "error"> {
  try {
    await save(choice);
  } catch {
    return "error";
  }
  try {
    await refresh();
  } catch {
    // The save itself worked; the plan will refetch on its next focus or poll.
  }
  return "saved";
}

/* ── "TripMuscle is watching" ───────────────────────────────────────────── */

const CHOICE_WORDS: Record<FareLevelChoice, string> = {
  cheapest: "the cheapest fare",
  changeable: "a fare you can change",
  extra_legroom: "extra legroom fares",
  premium: "premium fares",
};

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return items.slice(0, -1).join(", ") + " and " + items[items.length - 1];
}

/**
 * The reassurance line, as parts: what we watch for the fare, each offer, the
 * member price. Empty when we have no choice, no offers and no member price, so
 * nothing is claimed about a leg we are not tracking beyond the headline.
 */
export function watchingParts(input: {
  priceChoice?: PlanPriceChoice | null;
  focus?: Pick<PlanFlight, "yourPrice" | "memberPrice"> | null;
  offers?: readonly PlanOffer[] | null;
  memberPrice?: PlanMemberPrice | null;
  /** Today as YYYY-MM-DD; injected for tests. */
  today?: string;
}): string[] {
  const parts: string[] = [];
  const choice = input.priceChoice;
  if (choice) {
    const level = input.focus?.yourPrice ? shortLevel(input.focus.yourPrice.fareLevel) : CHOICE_WORDS[choice.fareLevel];
    const withs: string[] = [];
    if (choice.checkedBags > 0) withs.push(choice.checkedBags === 1 ? "1 bag" : choice.checkedBags + " bags");
    for (const k of choice.extras) withs.push(extraShortName(k));
    parts.push("fare drops on " + level + (withs.length ? " with " + joinAnd(withs) : ""));
  }
  // One offer is named; the rest are behind the "N offers" link beside the line.
  const featured = orderOffers(input.offers ?? [], input.today)[0];
  if (featured) {
    parts.push(
      featured.code && featured.code.trim()
        ? featured.carrierName + " " + featured.code.trim() + " promo"
        : featured.carrierName + " " + featured.title.toLowerCase(),
    );
  }
  const mp = input.memberPrice ?? input.focus?.memberPrice ?? null;
  if (mp && (mp.alreadyMember || mp.worthIt)) {
    parts.push(mp.alreadyMember ? "your " + mp.program + " price" : mp.program + " price");
  }
  return parts;
}

export function watchingLine(input: Parameters<typeof watchingParts>[0]): string | null {
  const parts = watchingParts(input);
  return parts.length ? "TripMuscle is watching: " + parts.join(" · ") : null;
}

/** A link is only rendered when it is an http(s) address. */
export function safeHref(url: string | null | undefined): string | null {
  return url && /^https?:\/\//i.test(url.trim()) ? url.trim() : null;
}

/**
 * Whether the "Your price" control has anything to price: a saved choice, or any
 * flight on the leg carrying fare levels, extras or a priced-for-you total.
 * Legs with none of that look exactly as they did.
 */
export function hasPriceChoiceData(leg: Pick<PlanLeg, "priceChoice" | "focus" | "matrix">): boolean {
  if (leg.priceChoice) return true;
  const flights: PlanFlight[] = leg.focus ? [leg.focus] : [];
  for (const g of leg.matrix ?? []) flights.push(...(g.flights ?? []));
  return flights.some((f) => (f.fares?.length ?? 0) > 1 || (f.extras?.length ?? 0) > 0 || !!f.yourPrice);
}

/* ── offers: which one leads ────────────────────────────────────────────── */

/** How many days away an offer's book-by is; null without one, negative when it has passed. */
export function daysUntil(iso: string | null | undefined, today: string): number | null {
  const d = parseDay(iso);
  const t = parseDay(today);
  if (!d || !t) return null;
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

/** Roughly what an offer is worth to a traveller, highest first. */
const OFFER_VALUE: Record<PlanOffer["kind"], number> = {
  promo_code: 8,
  fare_sale: 7,
  intro_fare: 6,
  free_bag: 5,
  companion: 5,
  membership_deal: 4,
  bonus_miles: 3,
  status_match: 2,
  other: 1,
};

/** Today's date as YYYY-MM-DD in the viewer's own calendar. */
export function todayIso(now: Date = new Date()): string {
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return now.getFullYear() + "-" + m + "-" + d;
}

/**
 * Offers in the order to show them: those with a live book-by first, soonest
 * deadline first, then the rest by value. Expired offers sink to the end.
 */
export function orderOffers(offers: readonly PlanOffer[], today: string = todayIso()): PlanOffer[] {
  const rank = (o: PlanOffer) => {
    const days = daysUntil(o.bookBy, today);
    if (days != null && days >= 0) return { group: 0, key: days };
    if (days != null && days < 0) return { group: 2, key: 0 };
    return { group: 1, key: 0 };
  };
  return offers
    .map((o, i) => ({ o, i, r: rank(o) }))
    .sort(
      (a, b) =>
        a.r.group - b.r.group ||
        a.r.key - b.r.key ||
        OFFER_VALUE[b.o.kind] - OFFER_VALUE[a.o.kind] ||
        a.i - b.i,
    )
    .map((x) => x.o);
}

/** Days within which a book-by counts as urgent enough to open the banner. */
export const URGENT_DAYS = 14;

/** True when some offer must be booked within the next two weeks. */
export function hasUrgentOffer(offers: readonly PlanOffer[], today: string = todayIso()): boolean {
  return offers.some((o) => {
    const d = daysUntil(o.bookBy, today);
    return d != null && d >= 0 && d <= URGENT_DAYS;
  });
}

export function offersLinkText(n: number): string {
  return n + (n === 1 ? " offer" : " offers") + " for this trip";
}

/* ── fly-days: when the strip earns its place ───────────────────────────── */

/**
 * Show the strip only when it changes a decision: the carrier has flights in the
 * options list, or it does not fly the leg's date.
 */
export function showFlyDays(fd: PlanFlyDays, leg: Pick<PlanLeg, "matrix">): boolean {
  if (fd.flysOnLegDate === false) return true;
  return (leg.matrix ?? []).some((g) => (g.flights ?? []).some((f) => f.airline === fd.carrier));
}
